<script setup lang="ts">
  import {
    Button,
    ButtonAppearance,
    ButtonSize,
    Checkbox,
    IconName,
    Input,
    InputSize,
    Picture,
    PictureFormat,
    Typo,
    TypoVariant,
    useToast,
  } from '@travelata-libs/ui-kit';
  import { ref } from 'vue';
  import type { Product } from '@/entities/product';
  import type { SearchCriteria } from '@/entities/searchCriteria';
  import { createSubscription } from '@/shared/api/createSubscription';
  import { getMindboxDeviceUuid } from '@/shared/lib/mindboxDeviceUuid';
  import BannerDecor from './BannerDecor.vue';
  import { HACKS8_POINT_OF_CONTACT } from '../config/pointOfContact';
  import { SERP_HACKS8_BANNER_TEXTS } from '../config/texts';
  import { isValidEmail } from '../lib/isValidEmail';

  interface Hacks8BannerProps {
    product: Product;
    searchCriteria: SearchCriteria;
    cardPosition: number;
  }

  defineProps<Hacks8BannerProps>();

  const { showToast } = useToast();

  const email = ref('');
  const error = ref('');
  const consent = ref(true);
  const consentError = ref(false);
  const loading = ref(false);
  const isSubmitted = ref(false);

  const getEmailError = (value: string): string => {
    if (!value.trim()) return SERP_HACKS8_BANNER_TEXTS.emailRequired;
    if (!isValidEmail(value)) return SERP_HACKS8_BANNER_TEXTS.emailInvalid;
    return '';
  };

  const validate = (): boolean => {
    error.value = getEmailError(email.value);
    consentError.value = !consent.value;

    return !error.value && !consentError.value;
  };

  const onInput = (value: string): void => {
    email.value = value;
    if (error.value) error.value = '';
  };

  const onConsentChange = (value: boolean): void => {
    consent.value = value;
    consentError.value = false;
  };

  const submit = async (): Promise<void> => {
    if (loading.value || !validate()) return;

    loading.value = true;
    try {
      await createSubscription({
        email: email.value.trim(),
        pointOfContact: HACKS8_POINT_OF_CONTACT,
        deviceUUID: getMindboxDeviceUuid(),
      });
      isSubmitted.value = true;
    } catch {
      showToast({
        type: 'error',
        mobile: true,
        title: SERP_HACKS8_BANNER_TEXTS.errorTitle,
        description: SERP_HACKS8_BANNER_TEXTS.errorDescription,
      });
    } finally {
      loading.value = false;
    }
  };
</script>

<template>
  <section
    class="hacks8-banner"
    data-testid="hacks8-banner"
  >
    <Picture
      class="hacks8-banner__picture"
      path="illustrations/hotel.webp"
      :extensions="[PictureFormat.WEBP]"
      :high-resolution="true"
      fit="contain"
      aria-hidden="true"
      data-testid="hacks8-banner-picture"
    />

    <div class="hacks8-banner__card">
      <BannerDecor />

      <div class="hacks8-banner__content">
        <Typo
          v-if="isSubmitted"
          class="hacks8-banner__success"
          :variant="TypoVariant.Heading5"
          tag="p"
          data-testid="hacks8-banner-success"
        >
          {{ SERP_HACKS8_BANNER_TEXTS.successTitle }}
        </Typo>

        <template v-else>
          <div class="hacks8-banner__text">
            <Typo
              :variant="TypoVariant.Heading5"
              tag="p"
            >
              {{ SERP_HACKS8_BANNER_TEXTS.title }}
            </Typo>
            <Typo
              class="hacks8-banner__description"
              :variant="TypoVariant.BodyMRegular"
              tag="p"
            >
              {{ SERP_HACKS8_BANNER_TEXTS.description }}
            </Typo>
          </div>

          <div class="hacks8-banner__form">
            <div class="hacks8-banner__row">
              <Input
                class="hacks8-banner__email"
                :model-value="email"
                :placeholder="SERP_HACKS8_BANNER_TEXTS.placeholder"
                :error="error"
                :size="InputSize.M"
                name="email"
                data-testid="hacks8-banner-email"
                @update:modelValue="onInput"
              />
              <Button
                class="hacks8-banner__submit"
                :appearance="ButtonAppearance.Tertiary"
                :size="ButtonSize.M"
                :icon-left="IconName.ChevronRight"
                :loading="loading"
                :title="SERP_HACKS8_BANNER_TEXTS.submit"
                data-testid="hacks8-banner-submit"
                @click="submit"
              />
            </div>

            <Checkbox
              class="hacks8-banner__consent"
              :model-value="consent"
              :error="consentError"
              appearance="dark"
              :mobile="true"
              name="consent"
              data-testid="hacks8-banner-consent"
              @update:modelValue="onConsentChange"
            >
              <Typo
                class="hacks8-banner__consent-label"
                :variant="TypoVariant.BodyMRegular"
              >
                {{ SERP_HACKS8_BANNER_TEXTS.consent }}
              </Typo>
            </Checkbox>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
  $picture-height: 240px;
  $picture-overlap: 80px;
  $picture-space: 124px;

  .hacks8-banner {
    align-items: center;
    display: flex;
    flex-direction: column;
    isolation: isolate;

    &__picture {
      height: $picture-height;
      position: relative;
      width: 288px;
      z-index: 1;
    }

    &__card {
      align-self: stretch;
      background: linear-gradient(31deg, #7158dc 0%, var(--color-purple-40) 100%);
      border-radius: var(--rounding-l);
      box-sizing: border-box;
      color: var(--color-text-inverse);
      margin-top: $picture-overlap - $picture-height;
      overflow: hidden;
      padding: calc(#{$picture-space} + var(--spacing-m) * 2) var(--spacing-m) var(--spacing-m);
      position: relative;
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
      position: relative;
    }

    &__success {
      align-self: center;
      max-width: 240px;
      text-align: center;
    }

    &__text {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxs);
    }

    &__description {
      max-width: 257px;
    }

    &__form {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-s);
    }

    &__row {
      align-items: flex-start;
      display: flex;
      gap: var(--spacing-xs);
    }

    &__email {
      flex: 1 1 auto;
      min-width: 0;
    }

    &__submit {
      border: none;
      flex-shrink: 0;
      padding: 0;
      width: 40px;
    }

    &__consent-label {
      color: var(--color-text-inverse);
      white-space: normal;
    }
  }
</style>
